import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { ReportsService } from '../../reports.service';
import { ToastrService } from 'ngx-toastr';
import { InbuiltUtilityService } from '../inbuilt-utility.service';
import { DatePipe } from '@angular/common';
import { BodyScrollEvent, ColDef, GridApi, GridReadyEvent, PostSortRowsParams, ColumnState } from 'ag-grid-community';
import { ExportDataService } from '../../../shared/services/export-data.service';

// ─── Types ────────────────────────────────────────────────────────────────────

export type MatrixColumnType = 'group' | 'count' | 'sum';
export type MatrixColumnConfig = { type: MatrixColumnType; columns: string };

interface MatrixNode {
  id: string;
  name: string;
  level: number;
  groupLabel: string;
  rowCount: number;
  counts: Record<string, number>;
  sums: Record<string, number>;
  /** Tracks whether any value encountered for that column had a $ prefix */
  sumHasDollar: Record<string, boolean>;
  /** Tracks distinct values per metric key when distinct count is required */
  distinctTracker: Record<string, Set<string>>;
  children: MatrixNode[];
  expanded: boolean;
}

interface MatrixGridRow {
  node: MatrixNode;
  group: string;
}

interface MatrixMetricRule {
  key: string;
  aggregate: 'sum' | 'count';
  distinct: boolean;
  sourceField?: string;
  distinctKey?: string;
  conditionField?: string;
  conditionValues: string[];
  /** When operation is 'mathematical', these are the column names to sum together (split by +/-). */
  sourceFields?: string[];
  operation?: string;
  /** Display order from columnInfo — used to sort columns when provided */
  order?: number;
  /** Optional formula to compute a percentage shown in brackets after the cell value.
   *  Column names in the formula are replaced with their aggregated values at render time.
   *  Example: "(Pending Charges * 100)/(Pending Charges + Completed Charges)" */
  showPercentage?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-inbuilt-matrix',
  standalone: false,
  templateUrl: './inbuilt-matrix-v2.component.html',
  styleUrl: './inbuilt-matrix-v2.component.css'
})
export class InbuiltMatrixV2Component implements OnInit, OnDestroy {

  @Input() columns: MatrixColumnConfig[] = [];
  @Input() config: any;
  @Input() elementData: any = null;
  @ViewChild('matrixContainer', { static: false }) private matrixContainer?: ElementRef<HTMLElement>;

  // ── UI state ──────────────────────────────────────────────────────────────
  loading = false;
  matrixLoader = false;
  showMatrix = false;
  allExpanded = false;
  isExporting = false;
  isDownloadDisabled = false;
  tableDataTotalCount = 0;
  maxResultCount = 0;

  // ── Data & time ───────────────────────────────────────────────────────────
  tableData: any[] = [];
  rawTableDataForDownload: any[] = [];
  currentDate: Date = new Date();
  currentTime = '';
  matrixGeneratedRowCount = 0;

  // ── AG Grid ───────────────────────────────────────────────────────────────
  matrixRowData: MatrixGridRow[] = [];
  matrixColumnDefs: ColDef[] = [];
  defaultColDef: ColDef = {
    sortable: false,
    filter: false,
    resizable: true
  };

  /** True once autoSizeAllColumns has been called after the first data load for the current dataset. */
  private metricColumnsAutoSized = false;
  private resizeDebounceHandle: any = null;

  // ── Parsed column lists ───────────────────────────────────────────────────
  groupColumns: string[] = [];
  countColumns: string[] = [];
  sumColumns: string[] = [];
  /** All column keys derived from the first row of the first API response */
  attributeColumns: string[] = [];

  // ── Internals ─────────────────────────────────────────────────────────────
  private matrixRoots: MatrixNode[] = [];
  private nodeMap = new Map<string, MatrixNode>();
  private resolvedGroupKeys: Array<string | undefined> = [];
  private resolvedCountKeys: Array<string | undefined> = [];
  private resolvedSumKeys: Array<string | undefined> = [];
  private selectedFilters: any = {};
  private gridApi: GridApi | null = null;

  private readonly refreshCancel$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();
  private readonly norm = (v: string) => (v ?? '').toLowerCase().replace(/[\s_\-]+/g, '');
  private groupByColumns: string[] = [];
  private metricRules: MatrixMetricRule[] = [];

  // Keep hierarchy intact by ordering visible rows with a tree-aware sorter after AG Grid computes sort state.
  readonly postSortRows = (params: PostSortRowsParams<MatrixGridRow>): void => {
    const ordered = this.buildSortedVisibleRowNodes(params.nodes || []);
    if (ordered.length !== params.nodes.length) {
      return;
    }

    params.nodes.length = 0;
    ordered.forEach((rowNode: any) => params.nodes.push(rowNode));
  };

  constructor(
    private reportsService: ReportsService,
    private toastr: ToastrService,
    private inbuiltUtilityService: InbuiltUtilityService,
    private datePipe: DatePipe,
    private exportDataService: ExportDataService
  ) {}

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.updateGmtTimestamp();
    this.hydrateColumnConfig();
    this.selectedFilters = this.config?.selectedFilters || {};
    this.subscribeToFilterEvents();
    // Fetch initial data
    this.showMatrix = true;
    this.resetAndFetch();
  }

  ngOnDestroy(): void {
    this.refreshCancel$.next();
    this.refreshCancel$.complete();
    this.destroy$.next();
    this.destroy$.complete();
    if (this.resizeDebounceHandle) clearTimeout(this.resizeDebounceHandle);
    // Avoid AG Grid API calls after teardown.
    this.gridApi = null;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.resizeDebounceHandle) clearTimeout(this.resizeDebounceHandle);
    this.resizeDebounceHandle = setTimeout(() => {
      if (!this.canUseGridApi()) return;
      // Reset to natural content widths first — columns previously stretched to fill a
      // large screen would otherwise look "overflowing" once the viewport shrinks (e.g.
      // browser zoom-in), causing the fit check below to wrongly skip re-fitting.
      this.gridApi!.autoSizeAllColumns();
      this.fitColumnsToAvailableWidth();
    }, 150);
  }

  // ─── Filter event subscription ──────────────────────────────────────────────

  private subscribeToFilterEvents(): void {
    this.inbuiltUtilityService.getSelectedFilterEvents()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        if (!this.shouldApplyFilterEvent(event)) return;

        // if (event?.showTable === false) {
        //   this.showMatrix = false;
        //   this.clearAll();
        //   return;
        // }

        this.showMatrix = true;
        this.selectedFilters = event?.selectedFilters || {};
        this.resetAndFetch();
      });
  }

  private shouldApplyFilterEvent(event: any): boolean {
    if (!event || !this.elementData || !this.config) return false;

    const evReport = String(event?.reportNameFromQueryParams ?? '');
    const evPage   = String(event?.currentPage?.key ?? event?.page ?? '');
    const evTab    = String(event?.currentTab?.key  ?? event?.tab  ?? '');
    const myReport = String(this.config?.reportNameFromQueryParams ?? '');
    const myPage   = String(this.config?.currentPage?.key ?? '');
    const myTab    = String(this.config?.currentTab?.key  ?? '');

    if (evReport !== myReport || evPage !== myPage || evTab !== myTab) return false;
    if (typeof event?.showTable === 'boolean') return true;

    const filterName = String(event?.filterName ?? '');
    if (!filterName) return false;

    const rawDeps = this.elementData?.filterList;
    if (!rawDeps) return true;

    let deps: any[] = [];
    if (Array.isArray(rawDeps)) {
      deps = rawDeps;
    } else if (typeof rawDeps === 'string') {
      try { deps = JSON.parse(rawDeps); } catch { deps = [rawDeps]; }
    }
    if (deps.length === 0) return true;

    return deps.some((dep: any) => {
      const k = dep && typeof dep === 'object' ? String(Object.keys(dep)[0]   ?? '') : String(dep ?? '');
      const v = dep && typeof dep === 'object' ? String(Object.values(dep)[0] ?? '') : String(dep ?? '');
      return k === filterName || v === filterName || String(dep ?? '') === filterName;
    });
  }

  // ─── Data fetching ──────────────────────────────────────────────────────────

  private resetAndFetch(): void {
    this.refreshCancel$.next();
    this.tableData = [];
    this.rawTableDataForDownload = [];
    this.tableDataTotalCount = 0;
    this.maxResultCount = 0;
    this.matrixGeneratedRowCount = 0;
    this.attributeColumns = [];
    this.metricColumnsAutoSized = false;
    this.clearMatrixData();
    this.matrixLoader = true;
    this.fetchMatrixData();
  }

  private fetchMatrixData(): void {
    this.loading = true;
    this.updateGmtTimestamp();

    const payload = {
      report:       this.config?.reportNameFromQueryParams,
      page:         this.config?.currentPage?.key || '',
      tab:          this.config?.currentTab?.key  || '',
      elementName:  this.elementData?.name  || '',
      elementType:  this.elementData?.type  || '',
      download:     false,
      selectedFilters: this.inbuiltUtilityService.getTablePayload(this.selectedFilters)
    };

    this.reportsService.getInbuildTableData(payload)
      .pipe(takeUntil(this.refreshCancel$), takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const newRows: any[] = Array.isArray(res?.data) ? res.data : [];
          const totalCount = Number(res?.totalCount ?? 0);
          this.tableDataTotalCount = totalCount || newRows.length;
          this.maxResultCount = totalCount || newRows.length;

          if (newRows.length > 0) {
            const resolvedColumnInfo = res?.columnInfo
              ?? this.elementData?.config?.columnInfo
              ?? this.config?.columnInfo;
            this.applyResponseConfig(res?.groupByColumns, resolvedColumnInfo, newRows);
            this.attributeColumns = Object.keys(newRows[0] || {});
            this.buildGridColumns();
            this.tableData = [...newRows];
            this.rawTableDataForDownload = [...newRows];
            this.appendRowsToMatrix(newRows);
          } else {
            this.clearMatrixData();
          }

          this.loading     = false;
          this.matrixLoader = false;
        },
        error: (err: any) => {
          this.loading      = false;
          this.matrixLoader = false;
          this.handleErrors(err);
        }
      });
  }

  private applyResponseConfig(groupByColumns: any, columnInfo: any, rows: any[]): void {
    const sampleRow = Array.isArray(rows) && rows.length > 0 ? rows[0] : {};
    const allKeys = Object.keys(sampleRow || {});

    const responseGroupBy = Array.isArray(groupByColumns)
      ? groupByColumns.map((item: any) => String(item ?? '').trim()).filter(Boolean)
      : [];

    const configuredGroupBy = this.resolveGroupByColumns();
    const preferredGroupBy = responseGroupBy.length > 0 ? responseGroupBy : configuredGroupBy;

    this.groupByColumns = preferredGroupBy;
    this.groupColumns = [...preferredGroupBy];

    this.metricRules = this.buildMetricRules(columnInfo, allKeys, this.groupColumns);
    this.countColumns = this.metricRules.filter((rule: MatrixMetricRule) => rule.aggregate === 'count').map((rule: MatrixMetricRule) => rule.key);
    this.sumColumns = this.metricRules.filter((rule: MatrixMetricRule) => rule.aggregate === 'sum').map((rule: MatrixMetricRule) => rule.key);

    // Fallback when backend doesn't provide useful columnInfo details.
    if (this.metricRules.length === 0 && allKeys.length > 0) {
      const groupSet = new Set(this.groupColumns.map((key: string) => this.norm(key)));
      const fallbackMetricColumns = allKeys.filter((key: string) => !groupSet.has(this.norm(key)));
      this.metricRules = fallbackMetricColumns.map((key: string) => ({
        key,
        aggregate: 'sum',
        distinct: false,
        conditionValues: []
      }));
      this.sumColumns = [...fallbackMetricColumns];
      this.countColumns = [];
    }

    this.countColumns = [...new Set(this.countColumns)];
    this.sumColumns = [...new Set(this.sumColumns)];
  }

  // ─── Matrix tree ────────────────────────────────────────────────────────────

  private buildMetricRules(columnInfo: any, allKeys: string[], groupColumns: string[]): MatrixMetricRule[] {
    if (!columnInfo || typeof columnInfo !== 'object' || Array.isArray(columnInfo)) {
      return [];
    }

    const groupSet = new Set(groupColumns.map((key: string) => this.norm(key)));
    const rules: MatrixMetricRule[] = [];

    for (const [rawKey, rawDefinition] of Object.entries(columnInfo)) {
      const resolvedKey = allKeys.find((key: string) => this.norm(key) === this.norm(String(rawKey))) || String(rawKey);
      if (!resolvedKey || groupSet.has(this.norm(resolvedKey))) {
        continue;
      }

      const parsedRule = this.parseMetricDefinition(rawDefinition, resolvedKey, allKeys);
      if (!parsedRule) {
        continue;
      }

      // Carry order from the definition object when present
      if (rawDefinition && typeof rawDefinition === 'object' && !Array.isArray(rawDefinition)) {
        const rawOrder = (rawDefinition as any).order;
        if (rawOrder !== undefined && rawOrder !== null && !isNaN(Number(rawOrder))) {
          parsedRule.order = Number(rawOrder);
        }
        // Extract optional showPercentage formula
        const rawPct = (rawDefinition as any).showPercentage;
        if (rawPct && typeof rawPct === 'string' && rawPct.trim()) {
          parsedRule.showPercentage = rawPct.trim();
        }
      }

      rules.push(parsedRule);
    }

    // Sort by order when at least one rule has it; rules without order keep their relative position at the end
    const hasAnyOrder = rules.some((r: MatrixMetricRule) => r.order !== undefined);
    if (hasAnyOrder) {
      rules.sort((a: MatrixMetricRule, b: MatrixMetricRule) => {
        const aHasOrder = a.order !== undefined;
        const bHasOrder = b.order !== undefined;
        if (aHasOrder && bHasOrder) return (a.order as number) - (b.order as number);
        if (aHasOrder) return -1;
        if (bHasOrder) return 1;
        return 0;
      });
    }

    return rules;
  }

  private parseMetricDefinition(definition: any, resolvedKey: string, allKeys: string[]): MatrixMetricRule | null {
    if (typeof definition === 'string') {
      const aggregate = this.normalizeAggregate(definition);
      return aggregate
        ? { key: resolvedKey, aggregate, distinct: false, conditionValues: [] }
        : null;
    }

    if (!definition || typeof definition !== 'object' || Array.isArray(definition)) {
      return null;
    }

    const aggregate = this.normalizeAggregate(
      definition.aggregate
      ?? definition.aggregation
      ?? definition.type
      ?? definition.metric
      ?? definition.value
    );
    if (!aggregate) {
      return null;
    }

    const distinct = !!definition.distinct;
    const sourceFieldCandidate = definition.sourceField ?? definition.sourceKey ?? definition.source;
    const sourceField = sourceFieldCandidate
      ? this.resolveColumnName(allKeys, String(sourceFieldCandidate))
      : undefined;
    const distinctKeyCandidate = definition.distinctKey ?? definition.distinctOn ?? definition.key;
    const distinctKey = distinctKeyCandidate
      ? this.resolveColumnName(allKeys, String(distinctKeyCandidate))
      : (sourceField || resolvedKey);

    const operation = String(definition.operation ?? '').trim().toLowerCase();
    const mathExpression = String(definition.condition ?? '').trim();
    const hasMathematicalCondition = aggregate === 'sum' && this.isMathematicalCondition(mathExpression, allKeys);

    if (operation === 'mathematical' || hasMathematicalCondition) {
      const sourceFields = this.parseMathematicalSourceFields(mathExpression, allKeys);
      const mathDistinctKeyCandidate = definition?.distinctKey ?? definition?.distinctOn;
      const mathDistinctKey = mathDistinctKeyCandidate
        ? this.resolveColumnName(allKeys, String(mathDistinctKeyCandidate))
        : undefined;
      return {
        key: resolvedKey,
        aggregate,
        distinct: false,
        operation: 'mathematical',
        sourceFields,
        conditionValues: [],
        ...(mathDistinctKey ? { distinctKey: mathDistinctKey } : {}),
      };
    }

    const parsedCondition = this.parseMetricCondition(definition.condition, allKeys, resolvedKey);

    return {
      key: resolvedKey,
      aggregate,
      distinct,
      sourceField,
      distinctKey,
      conditionField: parsedCondition.field,
      conditionValues: parsedCondition.values,
    };
  }

  private normalizeAggregate(value: any): 'sum' | 'count' | null {
    const aggregate = String(value ?? '').trim().toLowerCase();
    if (aggregate === 'sum') {
      return 'sum';
    }
    if (aggregate === 'count') {
      return 'count';
    }
    return null;
  }

  private parseMetricCondition(rawCondition: any, allKeys: string[], fallbackField: string): { field?: string; values: string[] } {
    if (!rawCondition) {
      return { field: undefined, values: [] };
    }

    if (typeof rawCondition === 'object' && !Array.isArray(rawCondition)) {
      const fieldCandidate = rawCondition.field ?? rawCondition.key ?? rawCondition.column;
      const field = fieldCandidate ? this.resolveColumnName(allKeys, String(fieldCandidate)) : undefined;
      const valuesSource = rawCondition.values ?? rawCondition.value ?? [];
      const values = Array.isArray(valuesSource)
        ? valuesSource.map((v: any) => String(v ?? '').trim().toLowerCase()).filter(Boolean)
        : [String(valuesSource ?? '').trim().toLowerCase()].filter(Boolean);

      return { field, values };
    }

    const conditionText = String(rawCondition ?? '').trim();
    if (!conditionText) {
      return { field: undefined, values: [] };
    }

    const clauses = this.splitConditionClauses(conditionText);
    if (clauses.length === 0) {
      return { field: this.resolveColumnName(allKeys, fallbackField), values: [] };
    }

    const values: string[] = [];
    let field: string | undefined;

    for (const clause of clauses) {
      const parsedClause = this.parseSingleConditionClause(clause, allKeys, fallbackField);
      if (!field && parsedClause.field) {
        field = parsedClause.field;
      }
      values.push(...parsedClause.values);
    }

    return {
      field: field || this.resolveColumnName(allKeys, fallbackField),
      values: [...new Set(values.filter(Boolean))]
    };
  }

  private splitConditionClauses(conditionText: string): string[] {
    return String(conditionText ?? '')
      .trim()
      .split(/\s+(?:AND|OR)\s+/i)
      .map((part: string) => part.trim())
      .filter(Boolean);
  }

  private isMathematicalCondition(conditionText: string, allKeys: string[]): boolean {
    const expression = String(conditionText ?? '').trim();
    if (!expression) {
      return false;
    }

    if (/[=<>]/.test(expression) || /\b(?:and|or|in|like)\b/i.test(expression)) {
      return false;
    }

    const sourceFields = this.parseMathematicalSourceFields(expression, allKeys);
    if (sourceFields.length < 2) {
      return false;
    }

    return sourceFields.every((fieldName: string) =>
      allKeys.some((key: string) => this.norm(key) === this.norm(fieldName))
    );
  }

  /**
   * Parses a mathematical expression like "Today + Day_1 + Day_2"
   * and returns the resolved column names to sum together.
   */
  private parseMathematicalSourceFields(expression: string, allKeys: string[]): string[] {
    if (!expression) return [];
    return expression
      .split(/[+\-]/)
      .map((part: string) => part.trim())
      .filter(Boolean)
      .map((part: string) => this.resolveColumnName(allKeys, part));
  }

  private parseSingleConditionClause(clause: string, allKeys: string[], fallbackField: string): { field?: string; values: string[] } {
    const equalityIndex = clause.indexOf('==') >= 0 ? clause.indexOf('==') : clause.indexOf('=');
    if (equalityIndex < 0) {
      return { field: this.resolveColumnName(allKeys, fallbackField), values: [this.normalizeConditionValue(clause)] };
    }

    const operatorLength = clause.indexOf('==') >= 0 ? 2 : 1;
    const leftSide = clause.slice(0, equalityIndex).trim();
    const rightSide = clause.slice(equalityIndex + operatorLength).trim();

    return {
      field: this.resolveColumnName(allKeys, leftSide || fallbackField),
      values: this.extractConditionValues(rightSide)
    };
  }

  private extractConditionValues(input: string): string[] {
    const cleaned = String(input ?? '')
      .replace(/[()]/g, '')
      .replace(/["']/g, '')
      .trim();

    if (!cleaned) {
      return [];
    }

    return cleaned
      .split(',')
      .map((item: string) => item.trim())
      .filter(Boolean)
      .map((item: string) => item.toLowerCase());
  }

  private normalizeConditionValue(value: string): string {
    return String(value ?? '').trim().toLowerCase();
  }

  private resolveColumnName(allKeys: string[], candidate: string): string {
    const normalizedCandidate = this.norm(candidate);
    return allKeys.find((key: string) => this.norm(key) === normalizedCandidate) || candidate;
  }

  private appendRowsToMatrix(rows: any[]): void {
    if (!rows?.length) return;

    this.resolveColumnKeysIfNeeded(rows[0]);

    for (const row of rows) {
      let parentId   = '';
      let parentNode: MatrixNode | undefined;

      for (let lvl = 0; lvl < this.groupColumns.length; lvl++) {
        const groupLabel  = this.groupColumns[lvl];
        const resolvedKey = this.resolvedGroupKeys[lvl];
        const rawValue    = resolvedKey ? row[resolvedKey] : this.resolveRowValue(row, groupLabel);
        const cellValue   = String(rawValue ?? '').trim() || 'N/A';
        if (cellValue.toLowerCase() === 'n/a') {
          // Skip this level but continue — child levels may still have valid
          // values (e.g. "No Mapping" rows) and should remain visible.
          continue;
        }
        const nodeId      = parentId ? `${parentId}||${cellValue}` : cellValue;

        let node = this.nodeMap.get(nodeId);
        if (!node) {
          node = {
            id:           nodeId,
            name:         cellValue,
            level:        lvl,
            groupLabel,
            rowCount:     0,
            counts:       this.createEmptyCounts(),
            sums:         this.createEmptySums(),
            sumHasDollar: this.createEmptySumHasDollar(),
            distinctTracker: this.createDistinctTracker(),
            children:     [],
            expanded:     false
          };
          this.nodeMap.set(nodeId, node);
          if (!parentNode) {
            this.matrixRoots.push(node);
          } else {
            parentNode.children.push(node);
          }
        }

        node.rowCount += 1;
        parentNode = node;
        parentId   = nodeId;
      }

      // Only accumulate at the deepest node reached for this row.
      // Parents will receive counts via bottom-up rollup after all rows
      // are processed, ensuring parent = sum of children (not a
      // re-deduplicated count across all descendant rows).
      if (parentNode) {
        this.accumulateColumns(parentNode, row);
      }
    }

    // Propagate leaf counts/sums up through the tree so each parent
    // equals the exact sum of its direct children.
    this.rollupToParents(this.matrixRoots);

    this.syncExpandCollapseState();

    this.refreshVisibleRows();
  }

  /**
   * Bottom-up rollup: children are processed first, then their
   * counts and sums are added to the parent. This ensures every
   * parent value equals the precise sum of its immediate children.
   */
  private rollupToParents(nodes: MatrixNode[]): void {
    for (const node of nodes) {
      if (!node.children.length) {
        continue;
      }
      // Recurse children first so grandchildren are already rolled up.
      this.rollupToParents(node.children);

      // Reset this node's own accumulated totals before summing children
      // (the node itself has no direct row accumulation after the change above).
      for (const col of this.countColumns) {
        node.counts[col] = 0;
        const isMathDistinctCount = this.metricRules.some(
          (r: MatrixMetricRule) => r.key === col && r.operation === 'mathematical' && r.distinctKey
        );
        if (isMathDistinctCount) {
          node.distinctTracker[col] = new Set<string>();
        }
      }
      for (const col of this.sumColumns) {
        node.sums[col] = 0;
        node.sumHasDollar[col] = false;
        // Reset the distinct set for mathematical+distinctKey sum columns
        const isMathDistinct = this.metricRules.some(
          (r: MatrixMetricRule) => r.key === col && r.operation === 'mathematical' && r.distinctKey
        );
        if (isMathDistinct) {
          node.distinctTracker[col] = new Set<string>();
        }
      }

      for (const child of node.children) {
        for (const col of this.countColumns) {
          const mathDistinctCountRule = this.metricRules.find(
            (r: MatrixMetricRule) => r.key === col && r.operation === 'mathematical' && r.distinctKey
          );
          if (mathDistinctCountRule) {
            const parentSet: Set<string> = node.distinctTracker[col] ?? (node.distinctTracker[col] = new Set<string>());
            const childSet: Set<string> | undefined = child.distinctTracker[col];
            if (childSet) {
              childSet.forEach((v: string) => parentSet.add(v));
            }
            node.counts[col] = parentSet.size;
          } else {
            node.counts[col] += child.counts[col] ?? 0;
          }
        }
        for (const col of this.sumColumns) {
          const mathDistinctRule = this.metricRules.find(
            (r: MatrixMetricRule) => r.key === col && r.operation === 'mathematical' && r.distinctKey
          );
          if (mathDistinctRule) {
            // Union child's distinct set into parent, then derive count
            const parentSet: Set<string> = node.distinctTracker[col] ?? (node.distinctTracker[col] = new Set<string>());
            const childSet: Set<string> | undefined = child.distinctTracker[col];
            if (childSet) {
              childSet.forEach((v: string) => parentSet.add(v));
            }
            node.sums[col] = parentSet.size;
          } else {
            node.sums[col] += child.sums[col] ?? 0;
            if (child.sumHasDollar[col]) {
              node.sumHasDollar[col] = true;
            }
          }
        }
      }
    }
  }

  /**
   * Accumulates count and sum values for a node from one raw data row.
   *  - count: numeric sum when row has a numeric value, else increments by 1.
   *          supports distinct count and conditional count when defined in columnInfo.
   *  - sum  : numeric parse after stripping `$` and `,`; displayed with compact currency.
   */
  private accumulateColumns(node: MatrixNode, row: any): void {
    const countRules = this.metricRules.filter((rule: MatrixMetricRule) => rule.aggregate === 'count');
    const sumRules = this.metricRules.filter((rule: MatrixMetricRule) => rule.aggregate === 'sum');

    for (const rule of countRules) {
      const col = rule.key;
      if (!this.passesCondition(rule, row)) {
        continue;
      }

      if (rule.operation === 'mathematical' && Array.isArray(rule.sourceFields) && rule.sourceFields.length > 0) {
        // distinctKey: track distinct IDs where any source field > 0, then count the set
        if (rule.distinctKey) {
          const hasAnyPositive = rule.sourceFields.some((fieldName: string) => {
            const rawStr = String(this.resolveRowValue(row, fieldName) ?? '').trim();
            if (!rawStr) return false;
            const parsed = this.parseNumeric(rawStr);
            return Number.isFinite(parsed) && parsed > 0;
          });
          if (hasAnyPositive) {
            const distinctSet: Set<string> = node.distinctTracker[col] ?? (node.distinctTracker[col] = new Set<string>());
            const distinctValue = String(this.resolveRowValue(row, rule.distinctKey) ?? '').trim();
            if (distinctValue) {
              distinctSet.add(distinctValue);
            }
          }
          node.counts[col] = (node.distinctTracker[col] ?? new Set()).size;
          continue;
        }

        let rowTotal = 0;
        for (const fieldName of rule.sourceFields) {
          const rawStr = String(this.resolveRowValue(row, fieldName) ?? '').trim();
          if (rawStr === '') {
            continue;
          }

          const parsed = this.parseNumeric(rawStr);
          if (Number.isFinite(parsed)) {
            rowTotal += parsed;
          }
        }

        node.counts[col] = (node.counts[col] || 0) + rowTotal;
        continue;
      }

      if (rule.distinct) {
        const distinctKey = this.resolveDistinctValueKey(rule, row);
        const distinctValueRaw = this.resolveRowValue(row, distinctKey);
        const distinctValue = String(distinctValueRaw ?? '').trim();
        if (!distinctValue) {
          continue;
        }

        const distinctSet = node.distinctTracker[col] || new Set<string>();
        if (!distinctSet.has(this.norm(distinctValue))) {
          distinctSet.add(this.norm(distinctValue));
          node.distinctTracker[col] = distinctSet;
          node.counts[col] = (node.counts[col] || 0) + 1;
        }
        continue;
      }

      const rawValue = this.resolveRowValue(row, col);
      const rawString = String(rawValue ?? '').trim();
      if (!rawString) {
        continue;
      }

      const numeric = this.parseNumeric(rawString);
      if (Number.isFinite(numeric)) {
        node.counts[col] = (node.counts[col] || 0) + numeric;
      } else {
        node.counts[col] = (node.counts[col] || 0) + 1;
      }
    }

    for (const rule of sumRules) {
      const col = rule.key;

      // Mathematical operation: sum values from multiple source columns per row
      if (rule.operation === 'mathematical' && Array.isArray(rule.sourceFields) && rule.sourceFields.length > 0) {
        // distinctKey: count distinct IDs where any source field has a value > 0
        if (rule.distinctKey) {
          const hasAnyPositive = rule.sourceFields.some((fieldName: string) => {
            const rawStr = String(this.resolveRowValue(row, fieldName) ?? '').trim();
            if (!rawStr) return false;
            const parsed = this.parseNumeric(rawStr);
            return Number.isFinite(parsed) && parsed > 0;
          });
          if (hasAnyPositive) {
            const distinctSet: Set<string> = node.distinctTracker[col] ?? (node.distinctTracker[col] = new Set<string>());
            const distinctValue = String(this.resolveRowValue(row, rule.distinctKey) ?? '').trim();
            if (distinctValue) {
              distinctSet.add(distinctValue);
            }
          }
          node.sums[col] = (node.distinctTracker[col] ?? new Set()).size;
          continue;
        }

        let rowTotal = 0;
        let hasDollar = false;
        for (const fieldName of rule.sourceFields) {
          const rawStr = String(this.resolveRowValue(row, fieldName) ?? '').trim();
          if (rawStr === '') continue;
          if (rawStr.startsWith('$')) hasDollar = true;
          const parsed = this.parseNumeric(rawStr);
          if (Number.isFinite(parsed)) {
            rowTotal += parsed;
          }
        }
        if (hasDollar) node.sumHasDollar[col] = true;
        node.sums[col] = (node.sums[col] || 0) + rowTotal;
        continue;
      }

      // Standard sum: single column
      const rawStr = String(this.resolveRowValue(row, col) ?? '').trim();
      if (rawStr !== '') {
        if (rawStr.startsWith('$')) node.sumHasDollar[col] = true;
        const parsed = this.parseNumeric(rawStr);
        if (Number.isFinite(parsed)) {
          node.sums[col] = (node.sums[col] || 0) + parsed;
        }
      }
    }
  }

  private passesCondition(rule: MatrixMetricRule, row: any): boolean {
    if (!rule.conditionValues || rule.conditionValues.length === 0) {
      return true;
    }

    const fieldCandidates = [rule.conditionField, rule.sourceField, rule.distinctKey, rule.key].filter(Boolean) as string[];
    const conditionField = fieldCandidates.find((candidate: string) => {
      const fieldValueRaw = this.resolveRowValue(row, candidate);
      return String(fieldValueRaw ?? '').trim() !== '';
    }) || rule.conditionField || rule.key;

    const fieldValueRaw = this.resolveRowValue(row, conditionField);
    const fieldValue = String(fieldValueRaw ?? '').trim().toLowerCase();

    if (!fieldValue) {
      return false;
    }

    const expectedValues = rule.conditionValues.map((value: string) => value.toLowerCase());
    return expectedValues.includes(fieldValue);
  }

  private resolveDistinctValueKey(rule: MatrixMetricRule, row: any): string {
    const preferredCandidates = [
      rule.sourceField,
      rule.distinctKey,
      rule.key,
      'Apps'
    ].filter(Boolean) as string[];

    for (const candidate of preferredCandidates) {
      const value = this.resolveRowValue(row, candidate);
      if (String(value ?? '').trim() !== '') {
        return candidate;
      }
    }

    return rule.sourceField || rule.distinctKey || rule.key || 'Apps';
  }

  private parseNumeric(value: string): number {
    const normalized = String(value ?? '')
      .trim()
      .replace(/^\((.*)\)$/, '-$1')
      .replace(/\$/g, '')
      .replace(/,/g, '');

    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : NaN;
  }

  // ─── AG Grid column config ──────────────────────────────────────────────────

  private hydrateColumnConfig(): void {
    const cols = this.columns?.length ? this.columns : [];
    this.groupByColumns = this.resolveGroupByColumns();
    this.groupColumns = [...this.groupByColumns];
    this.countColumns = [];
    this.sumColumns   = [];

    for (const col of cols) {
      if (col.type === 'group') {
        if (this.groupColumns.length === 0) {
          this.groupColumns.push(...col.columns.split(':').map((s: string) => s.trim()).filter(Boolean));
        }
      } else if (col.type === 'count') {
        this.countColumns.push(...col.columns.split(',').map((s: string) => s.trim()).filter(Boolean));
      } else if (col.type === 'sum') {
        this.sumColumns.push(...col.columns.split(',').map((s: string) => s.trim()).filter(Boolean));
      }
    }

    this.groupColumns = [...new Set(this.groupColumns)];
    this.countColumns = [...new Set(this.countColumns)];
    this.sumColumns   = [...new Set(this.sumColumns)];
    this.buildGridColumns();
  }

  private buildGridColumns(): void {
    const groupHeader = this.groupByColumns.length > 0
      ? this.groupByColumns.join(', ')
      : 'Group';

    // Group column — pinned left so it stays fixed during horizontal scroll
    const groupColDef: ColDef = {
      headerName: groupHeader,
      field: 'group',
      pinned: 'left',
      sortable: true,
      resizable: true,
      cellStyle: { textAlign: 'left' },
      cellRenderer: (params: any) => {
        const row: MatrixGridRow = params.data;
        if (!row?.node) return '';
        const hasKids  = (row.node.children?.length ?? 0) > 0;
        const symbol   = hasKids ? (row.node.expanded ? '▼' : '▶') : '–';
        const pad      = row.node.level * 20;
        const weight   = hasKids ? '600' : '400';
        const cursor   = hasKids ? 'pointer' : 'default';
        return `<span style="display:inline-block;padding-left:${pad}px;font-weight:${weight};cursor:${cursor}">${symbol}&nbsp;${params.value ?? ''}</span>`;
      }
    };

    const countColDefs: ColDef[] = this.countColumns.map((col: string) => {
      const rule = this.metricRules.find((r: MatrixMetricRule) => r.key === col);
      const pctFormula = rule?.showPercentage;
      return {
        headerName: col,
        field: `count__${this.norm(col)}`,
        sortable: true,
        resizable: true,
        headerClass: 'matrix-center-header',
        valueGetter: (p: any) => p.data?.node?.counts?.[col] ?? 0,
        valueFormatter: pctFormula
          ? (p: any) => {
              const base = p.value ?? 0;
              const pct = this.evalPercentageFormula(pctFormula, p.data?.node);
              return pct !== null ? `${base} (${Math.round(pct)}%)` : String(base);
            }
          : undefined,
        cellStyle: { textAlign: 'center' }
      };
    });

    const sumColDefs: ColDef[] = this.sumColumns.map((col: string) => {
      const rule = this.metricRules.find((r: MatrixMetricRule) => r.key === col);
      const pctFormula = rule?.showPercentage;
      return {
        headerName: col,
        field: `sum__${this.norm(col)}`,
        sortable: true,
        resizable: true,
        headerClass: 'matrix-center-header',
        valueGetter: (p: any) => {
          const node: MatrixNode = p.data?.node;
          if (!node) return 0;
          return node.sums?.[col] ?? 0;
        },
        valueFormatter: (p: any) => {
          const formatted = this.formatCompactCurrency(Number(p.value ?? 0));
          if (!pctFormula) return formatted;
          const pct = this.evalPercentageFormula(pctFormula, p.data?.node);
          return pct !== null ? `${formatted} (${Math.round(pct)}%)` : formatted;
        },
        cellStyle: { textAlign: 'center' }
      };
    });

    // If any metric rule carries an explicit order, honour that order in the column definitions
    // instead of the default count-first-then-sum arrangement.
    const hasAnyOrder = this.metricRules.some((r: MatrixMetricRule) => r.order !== undefined);
    let metricColDefs: ColDef[];
    if (hasAnyOrder) {
      const countDefMap = new Map<string, ColDef>(countColDefs.map((c: ColDef) => [c.headerName as string, c]));
      const sumDefMap   = new Map<string, ColDef>(sumColDefs.map((c: ColDef)   => [c.headerName as string, c]));
      metricColDefs = this.metricRules
        .map((rule: MatrixMetricRule) => countDefMap.get(rule.key) ?? sumDefMap.get(rule.key))
        .filter((c): c is ColDef => !!c);
    } else {
      metricColDefs = [...countColDefs, ...sumColDefs];
    }

    this.matrixColumnDefs = [groupColDef, ...metricColDefs];
  }

  // ─── AG Grid event handlers ─────────────────────────────────────────────────

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
  }

  onSortChanged(): void {
    const sortState = this.getActiveSortState();
    this.sortTreeInPlace(this.matrixRoots, sortState);
    this.refreshVisibleRows();
  }

  private canUseGridApi(): boolean {
    return !!this.gridApi && !this.gridApi.isDestroyed();
  }

  private setGridRowDataSafely(rows: MatrixGridRow[]): void {
    if (!this.canUseGridApi()) {
      return;
    }
    this.gridApi!.setGridOption('rowData', rows);
  }

  onBodyScroll(_: BodyScrollEvent): void {
    // No-op: matrix data is fetched in a single API call.
    return;
  }

  private buildSortedVisibleRowNodes(inputRowNodes: any[]): any[] {
    const byId = new Map<string, any>();
    inputRowNodes.forEach((rowNode: any) => {
      const id = rowNode?.data?.node?.id;
      if (id) {
        byId.set(id, rowNode);
      }
    });

    const ordered: any[] = [];
    const walk = (nodes: MatrixNode[]) => {
      const sortedLevel = this.getSortedNodesForLevel(nodes || []);
      for (const node of sortedLevel) {
        const rowNode = byId.get(node.id);
        if (rowNode) {
          ordered.push(rowNode);
        }
        if (node.expanded && node.children?.length) {
          walk(node.children);
        }
      }
    };

    walk(this.matrixRoots);
    return ordered;
  }

  private sortTreeInPlace(nodes: MatrixNode[], sortState: ColumnState | null): void {
    if (sortState && nodes.length >= 2) {
      nodes.sort((a: MatrixNode, b: MatrixNode) => {
        const comparison = this.compareNodesBySortState(a, b, sortState);
        if (comparison !== 0) {
          return sortState.sort === 'asc' ? comparison : -comparison;
        }
        return String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, { sensitivity: 'base' });
      });
    }
    for (const node of nodes) {
      if (node.children?.length) {
        this.sortTreeInPlace(node.children, sortState);
      }
    }
  }

  private getSortedNodesForLevel(nodes: MatrixNode[]): MatrixNode[] {
    const sortState = this.getActiveSortState();
    if (!sortState || nodes.length < 2) {
      return [...nodes];
    }

    const sorted = [...nodes];
    sorted.sort((a: MatrixNode, b: MatrixNode) => {
      const comparison = this.compareNodesBySortState(a, b, sortState);
      if (comparison !== 0) {
        return sortState.sort === 'asc' ? comparison : -comparison;
      }
      // stable fallback for deterministic ordering
      return String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, { sensitivity: 'base' });
    });
    return sorted;
  }

  private getActiveSortState(): ColumnState | null {
    const states = this.gridApi?.getColumnState?.() || [];
    const active = states.find((state: ColumnState) => state.sort === 'asc' || state.sort === 'desc');
    return active || null;
  }

  private compareNodesBySortState(a: MatrixNode, b: MatrixNode, sortState: ColumnState): number {
    const colId = String(sortState.colId ?? '');
    if (colId === 'group') {
      return String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, { sensitivity: 'base' });
    }

    if (colId.startsWith('count__')) {
      const metricKey = this.findMetricKeyByColId(colId, 'count');
      const aVal = metricKey ? Number(a.counts?.[metricKey] ?? 0) : 0;
      const bVal = metricKey ? Number(b.counts?.[metricKey] ?? 0) : 0;
      return aVal - bVal;
    }

    if (colId.startsWith('sum__')) {
      const metricKey = this.findMetricKeyByColId(colId, 'sum');
      const aVal = metricKey ? Number(a.sums?.[metricKey] ?? 0) : 0;
      const bVal = metricKey ? Number(b.sums?.[metricKey] ?? 0) : 0;
      return aVal - bVal;
    }

    return 0;
  }

  private findMetricKeyByColId(colId: string, type: 'count' | 'sum'): string | null {
    const prefix = `${type}__`;
    const normalizedKey = colId.startsWith(prefix) ? colId.slice(prefix.length) : colId;
    const candidates = type === 'count' ? this.countColumns : this.sumColumns;
    return candidates.find((key: string) => this.norm(key) === normalizedKey) || null;
  }

  onCellClicked(event: any): void {
    if (event.colDef?.field !== 'group') return;
    const row = event.data as MatrixGridRow;
    if (row?.node?.children?.length) this.toggleNode(row.node);
  }

  downloadAndExportToExcel(): void {
    if (this.isDownloadDisabled) {
      return;
    }

    this.isExporting = true;
    this.isDownloadDisabled = true;

    if (!this.rawTableDataForDownload || this.rawTableDataForDownload.length === 0) {
      this.toastr.error('No data available to export', '', {
        timeOut: 3000,
        positionClass: 'toast-top-center'
      });
      this.stopExportLoader();
      return;
    }

    this.exportCurrentRowsToExcel(this.rawTableDataForDownload);
    this.stopExportLoader();
  }

  private exportCurrentRowsToExcel(rows: any[]): void {
    const rowsToExport = [...(rows || [])];
    if (rowsToExport.length === 0) {
      this.toastr.error('No data available to export', '', {
        timeOut: 3000,
        positionClass: 'toast-top-center'
      });
      return;
    }

    this.exportDataService.exportToExcel(
      this.config?.reportNameFromQueryParams || 'matrix-report',
      [rowsToExport]
    );
  }

  private stopExportLoader(): void {
    this.isExporting = false;
    this.isDownloadDisabled = false;
  }

  // ─── Expand / collapse ──────────────────────────────────────────────────────

  toggleNode(node: MatrixNode): void {
    node.expanded = !node.expanded;
    this.syncExpandCollapseState();
    this.refreshVisibleRows();
  }

  toggleExpandCollapseAll(): void {
    this.setExpandState(this.matrixRoots, !this.allExpanded);
    this.syncExpandCollapseState();
    this.refreshVisibleRows();
  }

  private setExpandState(nodes: MatrixNode[], expanded: boolean): void {
    for (const node of nodes) {
      node.expanded = expanded;
      if (node.children?.length) this.setExpandState(node.children, expanded);
    }
  }

  private syncExpandCollapseState(): void {
    this.allExpanded = this.hasAnyExpandedNode(this.matrixRoots);
  }

  private hasAnyExpandedNode(nodes: MatrixNode[]): boolean {
    for (const node of nodes || []) {
      if (node.expanded) {
        return true;
      }
      if (node.children?.length && this.hasAnyExpandedNode(node.children)) {
        return true;
      }
    }
    return false;
  }

  // ─── Visible row management ─────────────────────────────────────────────────

  private refreshVisibleRows(): void {
    const previousScrollTop = this.getGridBodyScrollTop();

    const rows: MatrixGridRow[] = [];
    const walk = (nodes: MatrixNode[]) => {
      for (const node of nodes) {
        rows.push({ node, group: this.formatGroupPath(node) });
        if (node.expanded && node.children?.length) walk(node.children);
      }
    };
    walk(this.matrixRoots);
    this.matrixRowData = rows;
    // Row count shows all nodes in the tree except the top-level (root) parents.
    this.matrixGeneratedRowCount = this.countAllMatrixRows(this.matrixRoots) - this.matrixRoots.length;
    this.setGridRowDataSafely(this.matrixRowData);

    // Re-size columns: all on first data load so metric columns fit their content;
    // group column only on subsequent expand/collapse (metric values don't change).
    setTimeout(() => {
      if (this.canUseGridApi()) {
        if (!this.metricColumnsAutoSized) {
          this.metricColumnsAutoSized = true;
          this.gridApi!.autoSizeAllColumns();
        } else {
          this.gridApi!.autoSizeColumns(['group']);
        }
        setTimeout(() => this.fitColumnsToAvailableWidth(), 0);
      }
    }, 50);

    // AG Grid can reset scroll on rowData replacement; restore user position.
    this.restoreGridBodyScrollTop(previousScrollTop);
  }

  private getGridBodyScrollTop(): number {
    const viewport = this.matrixContainer?.nativeElement?.querySelector('.ag-body-viewport') as HTMLElement | null;
    return viewport?.scrollTop ?? 0;
  }

  /** Content-sized columns can leave a large blank gap on wide screens; stretch them to
   *  fill the grid only when doing so wouldn't force a horizontal scrollbar. When the
   *  auto-sized content is already wider than the viewport, leave widths as-is so the
   *  user still gets a horizontal scroll instead of squeezed/truncated columns. */
  private fitColumnsToAvailableWidth(): void {
    if (!this.canUseGridApi()) return;
    const viewport = this.matrixContainer?.nativeElement?.querySelector('.ag-center-cols-viewport') as HTMLElement | null;
    if (!viewport) return;
    if (viewport.scrollWidth <= viewport.clientWidth) {
      // Pin the group column's own auto-sized width so sizeColumnsToFit only stretches the metric columns.
      const groupWidth = this.gridApi!.getColumn('group')?.getActualWidth();
      this.gridApi!.sizeColumnsToFit(
        groupWidth ? { columnLimits: [{ key: 'group', minWidth: groupWidth, maxWidth: groupWidth }] } : undefined
      );
    }
  }

  private restoreGridBodyScrollTop(scrollTop: number): void {
    setTimeout(() => {
      const viewport = this.matrixContainer?.nativeElement?.querySelector('.ag-body-viewport') as HTMLElement | null;
      if (viewport) {
        viewport.scrollTop = scrollTop;
      }
    }, 0);
  }

  private countAllMatrixRows(nodes: MatrixNode[]): number {
    let count = 0;
    const walk = (input: MatrixNode[]) => {
      for (const node of input || []) {
        count += 1;
        if (node.children?.length) {
          walk(node.children);
        }
      }
    };
    walk(nodes || []);
    return count;
  }

  private formatGroupPath(node: MatrixNode): string {
    return String(node?.name ?? '');
  }

  // ─── Key resolution & row value helpers ────────────────────────────────────

  private resolveColumnKeysIfNeeded(sampleRow: any): void {
    if (!sampleRow) return;
    const allKeys = Object.keys(sampleRow);
    if (this.resolvedGroupKeys.length === 0) {
      this.resolvedGroupKeys = this.groupColumns.map((g: string) =>
        allKeys.find((k: string) => this.norm(k) === this.norm(g)));
    }
    if (this.resolvedCountKeys.length === 0) {
      this.resolvedCountKeys = this.countColumns.map((c: string) =>
        allKeys.find((k: string) => this.norm(k) === this.norm(c)));
    }
    if (this.resolvedSumKeys.length === 0) {
      this.resolvedSumKeys = this.sumColumns.map((s: string) =>
        allKeys.find((k: string) => this.norm(k) === this.norm(s)));
    }
  }

  private resolveRowValue(row: any, key: string): string {
    const nk = this.norm(key);
    for (const col of Object.keys(row || {})) {
      if (this.norm(col) === nk) return String(row[col] ?? '').trim();
    }
    return '';
  }

  private createEmptyCounts(): Record<string, number> {
    return Object.fromEntries(this.countColumns.map((c: string) => [c, 0]));
  }

  private createEmptySums(): Record<string, number> {
    return Object.fromEntries(this.sumColumns.map((s: string) => [s, 0]));
  }

  private createEmptySumHasDollar(): Record<string, boolean> {
    return Object.fromEntries(this.sumColumns.map((s: string) => [s, false]));
  }

  private createDistinctTracker(): Record<string, Set<string>> {
    return Object.fromEntries(this.countColumns.map((c: string) => [c, new Set<string>()]));
  }

  /**
   * Evaluates a showPercentage formula against a MatrixNode's aggregated values.
   * Column names in the formula are replaced with their numeric values from node.sums / node.counts.
   * Returns the computed number (0–100+ range), or null if the formula cannot be evaluated.
   */
  private evalPercentageFormula(formula: string, node: MatrixNode | undefined): number | null {
    if (!formula || !node) return null;

    // Collect all column values; sums take precedence over counts for the same key.
    const colValues = new Map<string, number>();
    for (const [k, v] of Object.entries(node.counts || {})) {
      colValues.set(k, Number(v ?? 0));
    }
    for (const [k, v] of Object.entries(node.sums || {})) {
      colValues.set(k, Number(v ?? 0));
    }

    // Replace column names with numeric values, longest names first to avoid partial matches.
    let expr = formula;
    const sortedKeys = Array.from(colValues.keys()).sort((a, b) => b.length - a.length);
    for (const key of sortedKeys) {
      const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(escaped, 'gi');
      expr = expr.replace(re, String(colValues.get(key) ?? 0));
    }

    return this.safeArithmetic(expr.trim());
  }

  /**
   * Evaluates a pure arithmetic expression string containing only digits, decimal points,
   * +, -, *, /, and parentheses.  No eval() / Function() is used.
   */
  private safeArithmetic(expr: string): number | null {
    // Guard: allow only arithmetic characters to prevent injection.
    if (!/^[\d\s+\-*/().]+$/.test(expr)) return null;

    const tokens = expr.match(/[\d.]+|[+\-*/()]/g);
    if (!tokens) return null;

    let pos = 0;

    const parsePrimary = (): number => {
      if (tokens[pos] === '(') {
        pos++;                    // consume '('
        const val = parseAddSub();
        if (tokens[pos] === ')') pos++; // consume ')'
        return val;
      }
      return parseFloat(tokens[pos++] ?? '0');
    };

    const parseMulDiv = (): number => {
      let val = parsePrimary();
      while (pos < tokens.length && (tokens[pos] === '*' || tokens[pos] === '/')) {
        const op = tokens[pos++];
        const rhs = parsePrimary();
        val = op === '*' ? val * rhs : (rhs !== 0 ? val / rhs : 0);
      }
      return val;
    };

    const parseAddSub = (): number => {
      let val = parseMulDiv();
      while (pos < tokens.length && (tokens[pos] === '+' || tokens[pos] === '-')) {
        const op = tokens[pos++];
        const rhs = parseMulDiv();
        val = op === '+' ? val + rhs : val - rhs;
      }
      return val;
    };

    try {
      const result = parseAddSub();
      return isFinite(result) ? result : null;
    } catch {
      return null;
    }
  }

  private formatCompactCurrency(value: number): string {
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';

    if (absValue >= 1_000_000_000) {
      return `${sign}$${(absValue / 1_000_000_000).toFixed(2)}B`;
    }

    if (absValue >= 1_000_000) {
      return `${sign}$${(absValue / 1_000_000).toFixed(2)}M`;
    }

    if (absValue >= 1_000) {
      return `${sign}$${(absValue / 1_000).toFixed(2)}K`;
    }

    return `${sign}$${absValue.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }

  private updateGmtTimestamp(): void {
    const now = new Date();
    this.currentDate = now;
    this.currentTime = `${this.datePipe.transform(now, 'hh:mm a', 'GMT') || ''} GMT`;
  }

  private resolveGroupByColumns(): string[] {
    const configGroupByColumns = this.elementData?.config?.groupByColumns ?? this.config?.groupByColumns;

    if (Array.isArray(configGroupByColumns)) {
      return configGroupByColumns.map((item: any) => String(item ?? '').trim()).filter(Boolean);
    }

    if (typeof configGroupByColumns === 'string') {
      return configGroupByColumns.split(',').map((item: string) => item.trim()).filter(Boolean);
    }

    const legacyGroupColumns = this.elementData?.config?.groupColumns ?? this.config?.groupColumns;
    if (Array.isArray(legacyGroupColumns)) {
      return legacyGroupColumns.map((item: any) => String(item ?? '').trim()).filter(Boolean);
    }

    if (typeof legacyGroupColumns === 'string') {
      return legacyGroupColumns.split(':').map((item: string) => item.trim()).filter(Boolean);
    }

    return [];
  }

  // ─── Clear helpers ──────────────────────────────────────────────────────────

  private clearMatrixData(): void {
    this.matrixRoots = [];
    this.matrixRowData = [];
    this.matrixGeneratedRowCount = 0;
    this.nodeMap.clear();
    this.resolvedGroupKeys = [];
    this.resolvedCountKeys = [];
    this.resolvedSumKeys   = [];
    this.allExpanded = false;
    this.setGridRowDataSafely([]);
  }

  private handleErrors(err: any): void {
    console.error('Matrix error:', err);
    this.toastr.error('An error occurred while fetching matrix data. Please try again later.', 'Error', {
      timeOut: 5000,
      positionClass: 'toast-top-center',
    });
  }
}

